// @file The four personal subscription products shared with the iOS app through Universal Purchase.
import { STORE_PRODUCT_IDENTIFIERS, type StoreProductIdentifier } from '@padlet/desktop-shell-protocol/store-products'

const [GOLD_MONTHLY, GOLD_ANNUAL, PLATINUM_MONTHLY, PLATINUM_ANNUAL] = STORE_PRODUCT_IDENTIFIERS

const APP_STORE_PRODUCT_IDS = {
  gold: {
    annual: GOLD_ANNUAL,
    monthly: GOLD_MONTHLY,
  },
  platinum: {
    annual: PLATINUM_ANNUAL,
    monthly: PLATINUM_MONTHLY,
  },
} as const

type AppStoreTier = keyof typeof APP_STORE_PRODUCT_IDS
type AppStoreSchedule = keyof (typeof APP_STORE_PRODUCT_IDS)[AppStoreTier]
interface AppStorePlan {
  tier: AppStoreTier
  period: AppStoreSchedule
}
type AppStoreTransition = 'immediate' | 'deferred' | 'same'

const TIER_RANK: Record<AppStoreTier, number> = { gold: 1, platinum: 2 }

function appStoreProductId(tier: AppStoreTier, paymentSchedule: AppStoreSchedule): StoreProductIdentifier {
  return APP_STORE_PRODUCT_IDS[tier][paymentSchedule]
}

function appStoreProductPlan(productIdentifier: string): AppStorePlan | undefined {
  for (const tier of Object.keys(APP_STORE_PRODUCT_IDS) as AppStoreTier[]) {
    for (const period of Object.keys(APP_STORE_PRODUCT_IDS[tier]) as AppStoreSchedule[]) {
      if (appStoreProductId(tier, period) === productIdentifier) return { tier, period }
    }
  }
  return undefined
}

function appStoreTransition(
  current: AppStorePlan | undefined,
  target: AppStorePlan | undefined,
): AppStoreTransition | undefined {
  if (current === undefined || target === undefined) return undefined
  if (current.tier === target.tier && current.period === target.period) return 'same'
  return TIER_RANK[target.tier] > TIER_RANK[current.tier] ? 'immediate' : 'deferred'
}

export { APP_STORE_PRODUCT_IDS, appStoreProductId, appStoreProductPlan, appStoreTransition }
export type { AppStorePlan, AppStoreSchedule, AppStoreTier, AppStoreTransition }
